import { Attr, Calc, Column } from '@mode-switch/model';
import {
  DataViewResponseTypes,
  DataViewTypes,
  FlamingoTypes,
  SwitchAttrsDomain,
  SwitchViewDomain,
  SwitchViewResponseTypes,
} from '@mode/shared/contract-common';
import { Switch } from '@mode/shared/util-switch';

//
// Explanation of helpers.
//
// This file has helpers that translate between different version of attrs. All of these helpers
// are necessary today because we have new and legacy code that both interacts with attr resources
// slightly differently. As legacy code gets migrated, we'll be able to remove items here and standardize
// the approach. Until then, here's a quick overview of the types of entities handled here.
//
// AttrResource - This is the attr that comes back from rails only in new Angular flow contexts. This
//   type is not compatable with legacy flow model.js resources.
//
// LegacyAttrResource - This is the attr that comes back from rails in the legacy flow contexts. These
//   helpers are only used in legacy flow involving selected-report.state.
//
// AttrFeature - This is the attr as stored in the Angular feature state. This only is used in new
//   Angular flows. There is not a corrollary for the legacy flow because the resource is stored
//   directly in state.
//

/**
 * Make the attr used for api payload from calc.
 *
 * This function is independent of whether resources or features are used for state.
 */
export function makeRequestAttrFromCalc(
  attr: FlamingoTypes.ModeCalc | FlamingoTypes.ModeColumn
): DataViewResponseTypes.SwitchViewAttr.RequestAttr {
  const { name, role, desc, scale, source, type } = attr;

  const formula_type = JSON.stringify(attr.formula.type);
  const formula_source = attr.formula.source;
  const param: DataViewResponseTypes.SwitchViewAttr.RequestAttr = {
    name,
    desc,
    source,
    role,
    type,
    scale,
    formula_type,
    formula_source,
  };

  if (attr.formula.role?.name) {
    param.formula_role = JSON.stringify(attr.formula.role);
  }

  if (attr.color) {
    param.color = attr.color;
  }

  if (attr.format) {
    param.format = attr.format;
  }

  if (attr.defaultsAggregate) {
    param.defaults_aggregate = attr.defaultsAggregate;
  }

  return param;
}

/**
 * Generate a calc attr from an attr resource.
 */
export function makeCalcFromAttrResource(attr: SwitchViewResponseTypes.SwitchViewAttrResource): FlamingoTypes.ModeCalc {
  const { token, is_local: isLocal, color, format, defaults_aggregate: defaultsAggregate } = attr;

  const defaults = Switch.Calcs.defaults(attr.formula_type, defaultsAggregate);
  const base = Calc.make(
    attr.name,
    attr.formula_source,
    attr.type,
    attr.role,
    attr.scale,
    attr.formula_type,
    attr.formula_role,
    {
      desc: attr.desc,
      defaults,
    }
  );

  return {
    ...base,
    token,
    isLocal,
    color,
    format,
    defaultsAggregate,
  };
}

/**
 * Generate a column attr from an attr resource
 */
export function makeColumnFromAttrResource(
  attr: SwitchViewResponseTypes.SwitchViewAttrResource
): FlamingoTypes.ModeColumn {
  const { token, is_local: isLocal, color, format, defaults_aggregate: defaultsAggregate } = attr;

  const defaults = Switch.Columns.defaults(attr.name, attr.formula_type, defaultsAggregate);
  const base = Column.make(
    attr.name,
    attr.formula_source,
    attr.type,
    attr.role,
    attr.scale,
    attr.formula_type,
    attr.formula_role,
    {
      desc: attr.desc,
      defaults,
    }
  );

  return {
    ...base,
    token,
    isLocal,
    color,
    format,
    defaultsAggregate,
  };
}

/**
 * Make Attrs from the attrResources.
 */
export function makeAttrsFromResources(
  attrResources: SwitchViewResponseTypes.SwitchViewAttrResource[]
): FlamingoTypes.ModeAttr[] {
  const attrs: FlamingoTypes.ModeAttr[] = [];

  for (const attrResource of attrResources) {
    switch (attrResource.source) {
      case Attr.Source.Calc: {
        attrs.push(makeCalcFromAttrResource(attrResource));
        break;
      }
      case Attr.Source.Column: {
        attrs.push(makeColumnFromAttrResource(attrResource));
        break;
      }
    }
  }

  return attrs;
}

/**
 * Make a SwitchView that only represents the columns from a DatasetResult
 */
export function makeColumnViewFromResults(datasetResult: DataViewTypes.DatasetResult) {
  // @TODO: (@anthonysimone) [GF] This needs to be updated! Name should be `switch_view_token`, as that will be the unique idenfitier moving forward
  return Switch.Views.make(datasetResult.queryToken, datasetResult.queryRunToken, datasetResult.metaColumns, [], {
    weekStart: datasetResult.weekStart,
    yearStart: datasetResult.yearStart,
  });
}

/****************
 *
 * Following helpers are used for transforms related to feature state.
 *
 * These are leveraged in flows that depend on new Angular switchView feature state.
 *
 ****************/

export function makeRequestAttrsFromAttrFeatures(
  attrs: SwitchViewDomain.SwitchAttr[]
): DataViewResponseTypes.SwitchViewAttr.RequestAttr[] {
  return attrs.map((attr) => makeRequestAttrFromCalc(makeCalcFromAttrFeatureState(attr)));
}

export function makeFeatureAttrFromCalc(
  attr: FlamingoTypes.ModeCalc | FlamingoTypes.ModeColumn,
  withFakeToken = false
): SwitchViewDomain.SwitchAttr {
  const { name, formula, role, desc, scale, source, type, color, format, defaultsAggregate } = attr;
  const token = withFakeToken ? 'ref_' + Math.random() : attr.token;
  const formulaType = formula.type;
  const formulaSource = formula.source;
  const formulaRole = formula.role;

  return <SwitchViewDomain.SwitchAttr>{
    token,
    name,
    desc,
    source,
    type,
    role,
    scale,
    formulaType,
    formulaSource,
    formulaRole,
    color,
    format,
    defaultsAggregate,
  };
}

export function unsetSwitchViewAttrsIsLocal(switchView: SwitchViewDomain.SwitchView): SwitchViewDomain.SwitchView {
  const switchAttrs = switchView.switchAttrs.map((attr) => ({ ...attr, isLocal: void 0 }));
  return {
    ...switchView,
    switchAttrs,
  };
}

/**
 * Generate a calc attr from attr feature state
 */
export function makeCalcFromAttrFeatureState(attr: SwitchViewDomain.SwitchAttr): FlamingoTypes.ModeCalc {
  const { token, isLocal, color, format, defaultsAggregate } = attr;

  const defaults = Switch.Calcs.defaults(attr.formulaType, defaultsAggregate);
  const base = Calc.make(
    attr.name,
    attr.formulaSource,
    attr.type,
    attr.role,
    attr.scale,
    attr.formulaType,
    attr.formulaRole,
    {
      desc: attr.desc,
      defaults,
    }
  );

  return {
    ...base,
    token,
    isLocal,
    color,
    format,
    defaultsAggregate,
  };
}

/**
 * Generate a column attr from attr feature state
 */
export function makeColumnFromAttrFeatureState(attr: SwitchViewDomain.SwitchAttr): FlamingoTypes.ModeColumn {
  const { token, isLocal, color, format, defaultsAggregate } = attr;

  const defaults = Switch.Columns.defaults(attr.name, attr.formulaType, defaultsAggregate);
  const base = Column.make(
    attr.name,
    attr.formulaSource,
    attr.type,
    attr.role,
    attr.scale,
    attr.formulaType,
    attr.formulaRole,
    {
      desc: attr.desc,
      defaults,
    }
  );

  return {
    ...base,
    token,
    isLocal,
    color,
    format,
    defaultsAggregate,
  };
}

/**
 * Make Attrs from the attr feature state.
 */
export function makeAttrsFromFeatureState(featureAttrs: SwitchAttrsDomain.SwitchAttr[]): FlamingoTypes.ModeAttr[] {
  const attrs: FlamingoTypes.ModeAttr[] = [];

  for (const featureAttr of featureAttrs) {
    switch (featureAttr.source) {
      case Attr.Source.Calc: {
        attrs.push(makeCalcFromAttrFeatureState(featureAttr));
        break;
      }
      case Attr.Source.Column: {
        attrs.push(makeColumnFromAttrFeatureState(featureAttr));
        break;
      }
    }
  }

  return attrs;
}

/**
 * Make a SwitchView from a SwitchViewFeature, SwitchAttrFeature, and a DatasetResult
 */
export function makeViewFromFeatureAndResults(
  switchViewFeature: SwitchViewDomain.SwitchView,
  datasetResult: DataViewTypes.DatasetResult
) {
  const attrs = makeAttrsFromFeatureState(switchViewFeature.switchAttrs);
  // @TODO: (@anthonysimone) [GF] This needs to be updated! Name should be `switch_view_token`, as that will be the unique idenfitier moving forward
  return Switch.Views.make(datasetResult.queryToken, datasetResult.queryRunToken, datasetResult.metaColumns, attrs, {
    weekStart: switchViewFeature.weekStart,
    yearStart: switchViewFeature.yearStart,
  });
}

/**
 * Make a SwitchView that represents the columns from a DatasetResult and Attrs
 */
export function makeViewFromResultsAndAttrsFeature(
  datasetResult: DataViewTypes.DatasetResult,
  switchAttrs: SwitchAttrsDomain.SwitchAttr[]
) {
  const attrs = makeAttrsFromFeatureState(switchAttrs);
  // @TODO: (@anthonysimone) [GF] This needs to be updated! Name should be `switch_view_token`, as that will be the unique idenfitier moving forward
  return Switch.Views.make(datasetResult.queryToken, datasetResult.queryRunToken, datasetResult.metaColumns, attrs, {
    weekStart: datasetResult.weekStart,
    yearStart: datasetResult.yearStart,
  });
}
